import defaultOptions from '@atlassian/jira-common-constants/src/fetch-default-options';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { toProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants';
import type { State, TourState, ActionProps } from '../../types';

export type GetTourState = () => Promise<void>;

const getUrl = (baseUrl: string) => `${baseUrl}/rest/servicedesk/onboarding/latest/fsf`;

const fetchTourState = async (baseUrl: string): Promise<TourState> => {
	const url = getUrl(baseUrl);
	const response = await fetch(url, defaultOptions);

	if (!response.ok) {
		const text = await response.text();
		const error = new FetchError(response.status, text);
		// Still throw the error even if it's a client fetch error,
		// so we get the error path in the handling code.
		// We only want to skip analytics firing.
		if (!isClientFetchError(error)) {
			fireErrorAnalytics({
				error,
				meta: {
					id: 'getTourState_responseNotOK',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				attributes: {
					message: error.message,
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}
		throw error;
	}

	return response.json();
};

export const getTourState =
	({ baseUrl }: ActionProps) =>
	({ getState, setState }: StoreActionApi<State>): GetTourState =>
	async () => {
		const { data, isLoading } = getState();
		if (data || isLoading) {
			return;
		}

		setState({ error: undefined, isLoading: true });

		try {
			const result: TourState = await fetchTourState(baseUrl);
			const { seenProjectTour, projectId } = result;
			setState({
				data: {
					seenProjectTour,
					projectId: projectId === null ? null : toProjectId(projectId),
				},
				isLoading: false,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			if (!isClientFetchError(error)) {
				fireErrorAnalytics({
					error,
					meta: {
						id: 'getTourState',
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
					sendToPrivacyUnsafeSplunk: true,
				});
			}
			setState({
				error,
				isLoading: false,
			});
			if (!isClientFetchError(error)) {
				throw error;
			}
		}
	};
